// Session tracking hook with structured logging
import { useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { createLogger } from '@/lib/structuredLogger';

const logger = createLogger('SessionTracking');

/**
 * Hook to track user session activity
 * Uses throttled logSessionActivity from AuthProvider context
 * Updates last_activity_at timestamp on user interactions
 */
export const useSessionTracking = (
  user: User | null,
  logSessionActivity: (activityType: string, metadata?: Record<string, unknown>) => Promise<void>,
) => {
  useEffect(() => {
    if (!user || !logSessionActivity) return;

    // Track initial session start (deduplicated: max once per hour via localStorage)
    const trackSessionStart = async () => {
      try {
        const SESSION_START_KEY = 'vw_session_start_last';
        const SESSION_START_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour
        const lastStr = localStorage.getItem(SESSION_START_KEY);
        if (lastStr && Date.now() - parseInt(lastStr, 10) < SESSION_START_COOLDOWN_MS) {
          return;
        }
        await logSessionActivity('session_start', {
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        });
        localStorage.setItem(SESSION_START_KEY, Date.now().toString());
      } catch (error) {
        logger.error('Failed to track session start', { error });
      }
    };

    trackSessionStart();

    // Track activity on user interactions (throttled by logSessionActivity)
    const trackActivity = async () => {
      if (!user) return;

      try {
        await logSessionActivity('session_activity', {
          timestamp: new Date().toISOString(),
        });
      } catch (err) {
        // Silent fail - don't interrupt user experience
        logger.debug('Activity tracking error', { error: err });
      }
    };

    // Debounce activity tracking to avoid excessive calls (in addition to throttling)
    let activityTimeout: NodeJS.Timeout;
    const debouncedTrackActivity = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(trackActivity, 60000); // Track once per minute max
    };

    // Listen to user interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => {
      window.addEventListener(event, debouncedTrackActivity);
    });

    // Track session end on page unload
    const trackSessionEnd = async () => {
      if (!user) return;

      try {
        // Quick non-blocking call on page unload
        await logSessionActivity('session_end', {
          timestamp: new Date().toISOString(),
        });
      } catch (err) {
        // Silent fail on unload
        logger.debug('Session end tracking error', { error: err });
      }
    };

    window.addEventListener('beforeunload', trackSessionEnd);

    // Cleanup
    return () => {
      clearTimeout(activityTimeout);
      events.forEach((event) => {
        window.removeEventListener(event, debouncedTrackActivity);
      });
      window.removeEventListener('beforeunload', trackSessionEnd);
    };
  }, [user, logSessionActivity]);
};
